import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, cardClass, IconButton, LoadingState, Select } from '../../components/ui'
import { HelpTip } from '../../components/ui/HelpTip'
import { api } from '../../lib/api'
import {
  adjacentWindows,
  browserOffsetMinutes,
  bucketByDay,
  formatDuration,
  mergeCalendars,
  pickerPeople,
  requestWindow,
  type CalendarEventsResponse,
  type CalendarPeopleResponse,
} from '../../lib/calendar'
import { dayTotals, formatHours, type HourShare } from '../../lib/calendarDayTotals'
import { useDays, useLeave } from '../../lib/time'
import { addMonths, monthGrid, monthStart, today } from '../time/dates'
import {
  CALENDAR_SCOPES,
  readOnlyCompany,
  scopeControlShown,
  scopeFlags,
  scopeOf,
  storeOnlyCompany,
  type CalendarScope,
} from '../../lib/calendarFilter'
import { DayEventList, tone } from './DayEventList'
import {
  CALENDAR_STALE_MS,
  personCalendarQuery,
  usePersonCalendar,
  type CalWindow,
} from './calendarQueries'
import { dayEventsTotal, dayPanelNotice, sourceMessages } from './dayEventsTotal'
import { dateFormatter } from '../../lib/dateFormat'

function useCalendarPeople(enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: ['calendar', 'people'],
    queryFn: async () => (await api.get<CalendarPeopleResponse>('/api/calendar/people')).data,
    staleTime: 5 * 60_000,
  })
}

function companyCalendarQuery(range: CalWindow) {
  return {
    queryKey: ['calendar', 'company', range.start, range.end],
    queryFn: async () =>
      (await api.get<CalendarEventsResponse>('/api/calendar/company/events', { params: range }))
        .data,
    staleTime: CALENDAR_STALE_MS,
  }
}

function useCompanyCalendar(enabled: boolean, range: CalWindow) {
  return useQuery({ ...companyCalendarQuery(range), enabled })
}

function ShareList({ title, rows, none }: { title: string; rows: HourShare[]; none: string }) {
  if (rows.length === 0) return null
  return (
    <div>
      <p className="text-xs text-slate-500 dark:text-slate-400">{title}</p>
      <ul className="mt-0.5 space-y-0.5 text-xs text-slate-700 dark:text-slate-200">
        {rows.map((r) => (
          <li key={r.label} className="flex justify-between gap-2">
            <span className="truncate">{r.label || none}</span>
            <span className="tabular-nums">{formatHours(r.hours)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function DayTotals({ personId, day }: { personId: string; day: string }) {
  const { t } = useTranslation()
  const dayLogs = useDays(personId, day, day)
  const dayLeave = useLeave('all', { from: day, to: day })
  const totals = dayLogs.data ? dayTotals(dayLogs.data, dayLeave.data ?? [], personId, day) : null
  if (!totals || !(totals.hasLogged || totals.hasPlan || totals.leaveType)) return null
  return (
      <div className="space-y-2 rounded-lg bg-slate-50 p-2 dark:bg-slate-900/40">
        <dl className="grid grid-cols-[auto_1fr] gap-x-2 text-xs">
          {totals.hasLogged && (
            <>
              <dt className="text-slate-500 dark:text-slate-400">{t('calendar.booked')}</dt>
              <dd className="font-medium tabular-nums text-slate-800 dark:text-slate-100">
                {formatHours(totals.loggedHours)}
                {totals.loggedStatus ? ` · ${totals.loggedStatus}` : ''}
              </dd>
            </>
          )}
          {totals.hasPlan && (
            <>
              <dt className="text-slate-500 dark:text-slate-400">{t('calendar.planned')}</dt>
              <dd className="tabular-nums text-slate-700 dark:text-slate-200">
                {formatHours(totals.plannedHours)}
              </dd>
            </>
          )}
          {totals.leaveType && (
            <>
              <dt className="text-slate-500 dark:text-slate-400">{t('calendar.away')}</dt>
              <dd className="text-slate-700 dark:text-slate-200">
                {totals.leaveType}
                {totals.leaveHalfDay ? ` · ${t('calendar.halfDay')}` : ''}
              </dd>
            </>
          )}
        </dl>
        <ShareList
          title={t('calendar.byProject')}
          rows={totals.byProject}
          none={t('calendar.noProject')}
        />
        <ShareList
          title={t('calendar.byActivity')}
          rows={totals.byActivity}
          none={t('calendar.noProject')}
        />
      </div>
  )
}

/** `companyOnly`: a reader without `staff` gets the company calendar alone; the people list and day logs are staff routes. */
export function CalendarSection({ companyOnly = false }: { companyOnly?: boolean } = {}) {
  const { t, i18n } = useTranslation()
  const [month, setMonth] = useState(() => monthStart(today()))
  const [selectedDay, setSelectedDay] = useState(() => today())
  const [chosen, setChosen] = useState<string | null>(null)
  const [showCompanyChoice, setShowCompany] = useState(true)
  const [onlyCompany, setOnlyCompany] = useState(readOnlyCompany)
  const companyView = companyOnly || onlyCompany
  const showCompany = companyView || showCompanyChoice

  const offset = browserOffsetMinutes()
  const range = useMemo(() => requestWindow(month, offset), [month, offset])

  const people = useCalendarPeople(!companyOnly)
  const me = people.data?.me ?? null
  const personId = companyView ? null : (chosen ?? me)
  const companyConfigured = companyOnly || (people.data?.company.configured ?? false)

  const person = usePersonCalendar(personId, range)
  const company = useCompanyCalendar(showCompany && companyConfigured, range)

  const queryClient = useQueryClient()
  const shownLoaded = (companyView || person.isFetched) && (!(showCompany && companyConfigured) || company.isFetched)
  useEffect(() => {
    if (!shownLoaded) return
    for (const w of adjacentWindows(month, offset)) {
      if (personId != null) void queryClient.prefetchQuery(personCalendarQuery(personId, w))
      if (showCompany && companyConfigured) void queryClient.prefetchQuery(companyCalendarQuery(w))
    }
  }, [shownLoaded, month, offset, personId, showCompany, companyConfigured, queryClient])

  const events = useMemo(
    () =>
      mergeCalendars(
        person.data?.state === 'ok' ? person.data.events : [],
        showCompany && company.data?.state === 'ok' ? company.data.events : [],
      ),
    [person.data, company.data, showCompany],
  )
  const byDay = useMemo(() => bucketByDay(events, offset), [events, offset])
  const grid = monthGrid(month)
  const dayEvents = byDay.get(selectedDay) ?? []
  const dayTotal = dayEventsTotal(dayEvents)

  const options = people.data ? pickerPeople(people.data.people, people.data.me) : []
  const { person: personMessage, company: companyMessage } = sourceMessages({
    peopleFailed: people.isError,
    person,
    company,
    companyShown: showCompany && companyConfigured,
  })

  const notice = dayPanelNotice({
    loading: people.isLoading || person.isLoading || company.isLoading,
    anySourceFailed: personMessage !== null || companyMessage !== null,
    eventCount: dayEvents.length,
  })

  const weekdayFmt = dateFormatter(i18n.language, { weekday: 'short', timeZone: 'UTC' })
  const monthFmt = dateFormatter(i18n.language, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
  const asUtc = (iso: string) => new Date(`${iso}T00:00:00Z`)
  const messageText = (key: string): string => {
    switch (key) {
      case 'calendar.stateNoMailbox':
        return t('calendar.stateNoMailbox')
      case 'calendar.stateNotOffered':
        return t('calendar.stateNotOffered')
      case 'calendar.stateNotConfigured':
        return t('calendar.stateNotConfigured')
      case 'calendar.stateConsentPending':
        return t('calendar.stateConsentPending')
      case 'calendar.loadFailed':
        return t('calendar.loadFailed')
      default:
        return t('calendar.stateUnavailable')
    }
  }

  function chooseScope(scope: CalendarScope) {
    const flags = scopeFlags(scope)
    setOnlyCompany(flags.onlyCompany)
    storeOnlyCompany(flags.onlyCompany)
    setShowCompany(flags.showCompany)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-1.5">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {t('app.calendar')}
          </h2>
          <HelpTip label={t('app.calendar')}>{t('calendar.hint')}</HelpTip>
        </div>
        {scopeControlShown(companyOnly, companyConfigured, onlyCompany) && (
          <div
            role="radiogroup"
            aria-label={t('calendar.scope')}
            className="inline-flex rounded-lg border border-border p-0.5"
          >
            {CALENDAR_SCOPES.map((scope) => {
              const on = scopeOf(onlyCompany, showCompanyChoice) === scope
              return (
                <button
                  key={scope}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => chooseScope(scope)}
                  className={
                    on
                      ? 'rounded-md bg-slate-900 px-3 py-1 text-sm font-medium text-white dark:bg-slate-100 dark:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'
                      : 'rounded-md px-3 py-1 text-sm text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/60'
                  }
                >
                  {t(`calendar.scopes.${scope}`)}
                </button>
              )
            })}
          </div>
        )}
        {!companyView && (
          <div className="flex items-center gap-2">
            <span aria-hidden="true" className="text-sm text-slate-700 dark:text-slate-200">
              {t('calendar.person')}
            </span>
            <Select
              label={t('calendar.person')}
              hideLabel
              inputClassName="py-1"
              value={personId ?? ''}
              onChange={(e) => setChosen(e.target.value || null)}
              disabled={!people.data}
            >
              {options.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.id === me ? ` ${t('calendar.me')}` : ''}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div className="ml-auto flex min-w-0 flex-wrap items-center gap-2">
          <IconButton
            variant="secondary"
            label={t('calendar.prevMonth')}
            icon={<ChevronLeft className="h-4 w-4" />}
            onClick={() => setMonth(addMonths(month, -1))}
          />
          <span className="text-center text-sm font-medium sm:min-w-36 text-slate-800 dark:text-slate-100">
            {monthFmt.format(asUtc(month))}
          </span>
          <IconButton
            variant="secondary"
            label={t('calendar.nextMonth')}
            icon={<ChevronRight className="h-4 w-4" />}
            onClick={() => setMonth(addMonths(month, 1))}
          />
          <Button
            onClick={() => {
              setMonth(monthStart(today()))
              setSelectedDay(today())
            }}
          >
            {t('calendar.thisMonth')}
          </Button>
        </div>
      </div>

      {(personMessage || companyMessage) && (
        <div className="space-y-1 rounded-xl border border-dashed border-slate-300 p-3 text-sm text-slate-500 dark:border-slate-600 dark:text-slate-400">
          {personMessage && <p>{messageText(personMessage)}</p>}
          {companyMessage && (
            <p>
              {t('calendar.company')}: {messageText(companyMessage)}
            </p>
          )}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[1fr_20rem]">
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          <div className="grid min-w-[36rem] grid-cols-7">
            {grid.slice(0, 7).map((d) => (
              <div
                key={`h-${d}`}
                className="border-b border-slate-200 bg-slate-50 p-1 text-center text-xs font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-400"
              >
                {weekdayFmt.format(asUtc(d))}
              </div>
            ))}
            {grid.map((d) => {
              const list = byDay.get(d) ?? []
              const inMonth = d.slice(0, 7) === month.slice(0, 7)
              const selected = d === selectedDay
              return (
                <button
                  type="button"
                  key={d}
                  onClick={() => setSelectedDay(d)}
                  aria-pressed={selected}
                  className={`flex min-h-24 flex-col gap-0.5 overflow-hidden border-b border-r border-slate-100 p-1 text-left dark:border-slate-700/60 ${
                    selected ? 'ring-2 ring-inset ring-blue-500' : ''
                  } ${inMonth ? '' : 'bg-slate-50 dark:bg-slate-900/40'}`}
                >
                  <span
                    className={`text-xs tabular-nums ${
                      d === today()
                        ? 'font-bold text-blue-700 dark:text-blue-300'
                        : inMonth
                          ? 'text-slate-700 dark:text-slate-200'
                          : 'text-slate-500 dark:text-slate-400'
                    }`}
                  >
                    {Number(d.slice(8))}
                  </span>
                  {list.slice(0, 3).map((e) => (
                    <span
                      key={`${e.source}-${e.id}`}
                      className={`truncate rounded px-1 text-[10px] leading-4 ${tone(e)}`}
                    >
                      {e.subject || t('calendar.noSubject')}
                    </span>
                  ))}
                  {list.length > 3 && (
                    <span className="text-[10px] text-slate-500 dark:text-slate-400">
                      {t('calendar.more', { count: list.length - 3 })}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <section className={`${cardClass} space-y-3`}>
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {dateFormatter(i18n.language, {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              timeZone: 'UTC',
            }).format(asUtc(selectedDay))}
          </h2>
          {personId != null && <DayTotals personId={personId} day={selectedDay} />}
          {notice === 'loading' && <LoadingState variant="inline" label={t('calendar.loading')} />}
          {notice && notice !== 'loading' && (
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('calendar.noEvents')}</p>
          )}
          {dayEvents.length > 0 && (
            <>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
              {t('calendar.dayTotal', { duration: formatDuration(dayTotal.minutes) })}
              {dayTotal.allDayLeftOut && (
                <span className="font-normal text-slate-500 dark:text-slate-400">
                  {' '}({t('calendar.allDayNotCounted')})
                </span>
              )}
            </p>
            <DayEventList events={dayEvents} offset={offset} />
            </>
          )}
        </section>
      </div>
    </div>
  )
}
